import { useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import Paginator from '~/components/Paginator';
import ApiErrorComponent from '~/partials/ApiErrorComponent';
import Search from '~/partials/Search';
import { ScrollArea } from '~/primitives/scroll-area';
import { Separator } from '~/primitives/separator';
import { useList } from '~/util/hooks/useList';
import {
    levelRecordsQuery,
    levelUpdatesQuery,
} from '~/util/queries/public/levels';
import { LevelResolvedRecord } from '~/util/types/submissions';
import { LevelUpdate } from '~/util/types/levels';
import { recordsSchemaInput } from './-SearchParams';
import { useLocaleDateString } from '~/util/hooks/useLocale';
import { useScopes } from '~/components/Providers/AuthProvider';
import { Button } from '~/primitives/button';
import { LevelRecordEntry } from '~/components/LevelRecordEntry';

const Route = getRouteApi('/list/$levelid');

type RecordsTimelineItem =
    | {
          kind: 'record';
          record: LevelResolvedRecord;
      }
    | {
          kind: 'update';
          update: LevelUpdate;
      };

function RecordsInfo() {
    const { levelid } = Route.useParams();
    const { page, search } = Route.useSearch({
        select: ({ records }) => records,
    });

    const level = Route.useLoaderData({
        select: ({
            id,
            name,
            level_id,
            points,
            two_player,
            status,
            position,
            requires_raw_footage,
        }) => ({
            id,
            name,
            level_id,
            points,
            two_player,
            status,
            position,
            requires_raw_footage,
        }),
    });
    const list = useList();

    const localeDate = useLocaleDateString();

    const { hasScope } = useScopes();

    const { data, error, isError } = useSuspenseQuery(
        levelRecordsQuery({
            list,
            levelid,
            page,
            per_page: 50,
            submitter_filter: search,
        }),
    );

    const updateNotes = useSuspenseQuery(
        levelUpdatesQuery({ list, levelid }),
    )?.data;

    const navigate = Route.useNavigate();
    const navigateRecords = (search: recordsSchemaInput) =>
        navigate({
            search: (prev) => ({
                ...prev,
                records: {
                    ...prev.records,
                    ...search,
                },
            }),
        });

    const mergedTimelineItems = addUpdatesToRecords({
        updates: updateNotes,
        records: data?.data ?? [],
        list,
        isFirstPage: (data?.page ?? page) === 1,
        isLastPage: (data?.page ?? page) >= (data?.pages ?? page),
    });

    if (isError) return <ApiErrorComponent {...error} />;

    const recordCount = data?.count ?? 0;
    const totalPages = data?.pages ?? 1;

    return (
        <div className="w-full h-full bg-background-dark rounded-xl items-center flex flex-col">
            <div className="flex flex-col w-11/12 mt-7 gap-2">
                <h2 className="border-b border-secondary-light pb-2 clamp-[text-xl-2xl-clamp] font-medium">
                    {recordCount > 0
                        ? `Records (${recordCount})`
                        : 'No records'}
                </h2>
                {hasScope('submission_edit_non_self_claimed') && (
                    <div className="flex gap-2 w-full justify-center">
                        <Link
                            to="/staff/submissions"
                            search={({ list }) => ({
                                list,
                                level,
                            })}
                        >
                            <Button
                                className="w-full gap-2"
                                variant="secondary"
                            >
                                Submissions <ExternalLink />
                            </Button>
                        </Link>
                        <Link
                            to="/staff/records"
                            search={({ list }) => ({
                                list,
                                level,
                            })}
                        >
                            <Button
                                className="w-full gap-2"
                                variant="secondary"
                            >
                                Records <ExternalLink />
                            </Button>
                        </Link>
                    </div>
                )}
                {(recordCount > 0 || search) && (
                    <>
                        <Search
                            initial_input={search}
                            onInputChange={(search: string) =>
                                navigateRecords({
                                    search,
                                    page: 1,
                                })
                            }
                        />
                        <ScrollArea className="w-full h-[calc(100vh-320px)] pr-3">
                            {mergedTimelineItems.map((item) =>
                                item.kind === 'record' ? (
                                    <LevelRecordEntry
                                        key={item.record.id}
                                        record={item.record}
                                    />
                                ) : (
                                    <div key={item.update.id} className="py-2">
                                        <div className="flex items-center gap-3">
                                            <Separator className="flex-1" />
                                            <span className="text-xs font-semibold uppercase text-secondary-light">
                                                {localeDate(
                                                    item.update.timestamp,
                                                )}
                                            </span>
                                            <Separator className="flex-1" />
                                        </div>
                                        <div className="py-2 text-center">
                                            <p className="font-semibold uppercase text-secondary-foreground">
                                                {item.update.update_type} Update
                                            </p>
                                            <p className="mt-1 text-secondary-light italic">
                                                {item.update.changelog}
                                            </p>
                                        </div>
                                        <Separator />
                                    </div>
                                ),
                            )}
                            {
                                <Paginator
                                    currentPage={page}
                                    onPageChange={(page) =>
                                        navigateRecords({
                                            page,
                                        })
                                    }
                                    totalPages={totalPages}
                                />
                            }
                        </ScrollArea>
                    </>
                )}
            </div>
        </div>
    );
}

export default RecordsInfo;

function addUpdatesToRecords({
    updates,
    records,
    list,
    isFirstPage,
    isLastPage,
}: {
    updates: LevelUpdate[];
    records: LevelResolvedRecord[];
    list: string;
    isFirstPage: boolean;
    isLastPage: boolean;
}) {
    if (records.length === 0) return [];

    if (list === 'platformer') {
        const platformerNotes = [...updates].sort((a, b) => {
            if (a.timestamp === b.timestamp) {
                return 0;
            }
            return a.timestamp! > b.timestamp! ? -1 : 1;
        });

        const recordItems = records.map((record) => ({
            kind: 'record' as const,
            record,
        }));

        if (!isFirstPage) return recordItems;

        return [
            ...platformerNotes.map((update) => ({
                kind: 'update' as const,
                update,
            })),
            ...recordItems,
        ];
    }

    const sortedUpdates = [...updates].sort((a, b) => {
        if (a.timestamp === b.timestamp) {
            return 0;
        }
        return a.timestamp! > b.timestamp! ? 1 : -1;
    });

    const items: RecordsTimelineItem[] = [];
    let noteIndex = 0;

    for (let recordIndex = 0; recordIndex < records.length; recordIndex++) {
        const record = records[recordIndex];

        while (
            noteIndex < sortedUpdates.length &&
            sortedUpdates[noteIndex].timestamp! <= record.achieved_at
        ) {
            if (isFirstPage || recordIndex > 0) {
                items.push({
                    kind: 'update',
                    update: sortedUpdates[noteIndex],
                });
            }
            noteIndex++;
        }

        items.push({
            kind: 'record',
            record,
        });
    }

    while (noteIndex < sortedUpdates.length) {
        if (isLastPage) {
            items.push({
                kind: 'update',
                update: sortedUpdates[noteIndex],
            });
        }
        noteIndex++;
    }

    return items;
}
